/**
 * Human-friendly date + time formatters for member-facing surfaces.
 *
 * Kept in the customer app rather than `@snootsvet/shared`: the copy
 * ("Today", "Tomorrow") and the choice of month-format for DOB are
 * specifically customer-portal UX choices, not shared data-formatting
 * concerns.
 *
 * Each formatter takes a `Region` so the same call renders in en-GB /
 * 24h for UK members and en-US / 12h for US members. Callers grab the
 * region from `useRegion()`.
 */

import type { Region } from '@snootsvet/shared/portal'

/**
 * "Today" / "Tomorrow" for anything within the next 48h; "Mon 14 Jul"
 * (en-GB) or "Mon, Jul 14" (en-US) otherwise.
 */
export function formatAppointmentDay(date: Date, region: Region, timezone?: string): string {
  const startOfDay = (d: Date) => {
    const copy = new Date(d)
    copy.setHours(0, 0, 0, 0)
    return copy
  }
  const dayDelta = Math.round(
    (startOfDay(date).getTime() - startOfDay(new Date()).getTime()) / 86_400_000
  )

  if (dayDelta === 0) return 'Today'
  if (dayDelta === 1) return 'Tomorrow'

  return date.toLocaleDateString(region.locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: timezone ?? region.timezone,
  })
}

/** Clock time, region-appropriate (24h UK, 12h US). */
export function formatAppointmentTime(date: Date, region: Region, timezone?: string): string {
  return date.toLocaleTimeString(region.locale, {
    hour: region.hour12 ? 'numeric' : '2-digit',
    minute: '2-digit',
    hour12: region.hour12,
    timeZone: timezone ?? region.timezone,
  })
}

/**
 * Formats an ISO date-of-birth string (yyyy-mm-dd) for display.
 * Falls back to the raw input if the string doesn't parse.
 *
 * Example: `'2020-03-14'` → `'14 March 2020'` (en-GB) or
 * `'March 14, 2020'` (en-US).
 */
export function formatDob(iso: string, region: Region): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleDateString(region.locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}
